import React, { useState, useEffect } from 'react';
// Import useAuth as default export
import useAuth from '../hooks/useAuth';
import AppLayout from './AppLayout';
import Dashboard from '../pages/Dashboard';
import SalesAnalytics from '../pages/SalesAnalytics';
import DeliveryAnalytics from '../pages/DeliveryAnalytics';
import PurchaseAnalytics from '../pages/PurchaseAnalytics';
import InventoryAnalytics from '../pages/InventoryAnalytics';
import CustomerAnalytics from '../pages/CustomerAnalytics';
import InquiryAnalytics from '../pages/InquiryAnalytics';
import FinancialAnalytics from '../pages/FinancialAnalytics';
import SupervisorAnalyticsPage from '../pages/SupervisorAnalyticsPage';
import ChallanAnalyticsPage from '../pages/ChallanAnalyticsPage';
import AssignedToChallanPage from '../pages/AssignedToChallanPage';
import MovementAnalyticsPage from '../pages/MovementAnalyticsPage';
import SalesStaffPage from '../pages/SalesStaffPage';
import Users from '../pages/Users';
import IpAddressesPage from '../pages/IpAddressesPage';
import Login from '../pages/login';
import CustomersPage from '../pages/CustomersPage';
import TaxesPage from '../pages/TaxesPage';
import HsnCodesPage from '../pages/HsnCodesPage';
import LocationsPage from '../pages/LocationsPage';
import ProductCataloguePage from '../pages/ProductCataloguePage';
import ProductCategoryPage from '../pages/ProductCategoryPage';
import ProductSizesPage from '../pages/ProductSizesPage';
import InquiriesPage from '../pages/InquiriesPage';
import WarehousesPage from '../pages/WarehousesPage';
import WarehouseInventoryPage from '../pages/WarehouseInventoryPage';
import OrdersPage from '../pages/OrdersPage';
import ConfirmedOrdersPage from '../pages/ConfirmedOrdersPage';
import PaymentsTrackingPage from '../pages/PaymentsTrackingPage';

import PurchaseOrdersPage from '../pages/PurchaseOrdersPage';
import PurchaseAdvicePage from '../pages/PurchaseAdvicePage';
import PurchaseChallanPage from '../pages/PurchaseChallanPage';
import PurchaseChallanPrintedPage from '../pages/PurchaseChallanPrintedPage';
import PurchaseDaybookPage from '../pages/PurchaseDaybookPage';
import InvoicePurchasePage from '../pages/InvoicePurchasePage';

import DeliveryOrdersPage from '../pages/DeliveryOrdersPage';
import DeliveryChallanPage from '../pages/DeliveryChallanPage';
import DeliveryChallanPrintedPage from '../pages/DeliveryChallanPrintedPage';
import UnprintedChallansPage from '../pages/UnprintedChallansPage';
import ChallanPrintVariancePage from '../pages/ChallanPrintVariancePage';
import InvoiceSalesPage from '../pages/InvoiceSalesPage';
import SalesDaybookPage from '../pages/SalesDaybookPage';
import VehicleTrackingPage from '../pages/VehicleTrackingPage';
import ProfilePage from '../pages/ProfilePage';
import ChannelSettingsPage from '../pages/ChannelSettingsPage';
import TallySettingsPage from '../pages/TallySettingsPage';
import ReportsPage from '../pages/ReportsPage';
import IntelligencePage from '../pages/IntelligencePage';
import AIChatPage from '../pages/AIChatPage';
import AIPurchasePlanPage from '../pages/AIPurchasePlanPage';
import { ChannelSettingsProvider } from '../context/ChannelSettingsContext';
import { PrintSettingsProvider } from '../context/PrintSettingsContext';
import { CoilSettingsProvider } from '../context/CoilSettingsContext';
import { OrderSettingsProvider } from '../context/OrderSettingsContext';

// eslint-disable-next-line no-unused-vars
import { SuperAdminGuard, AdminGuard, StaffGuard, RoleGuard, PermissionGuard } from './RoleGuards';
import ErrorBoundary from './common/ErrorBoundary';
import { USER_ROLES, PERMISSIONS } from '../utils/constants';
import { useWarehouseContext } from '../context/WarehouseContext';
import { useRoleAccess } from '../hooks/useRoleAccess';
import { setLocaleFromUserLanguage } from '../i18n';
import profileService from '../services/profileService';
import { useTranslation } from 'react-i18next';


/**
 * Main App Component
 * Handles routing between login and main app based on authentication
 * Shows role-based content once logged in
 */
const MainApp = () => {
  const { isAuthenticated, user, isInitializing } = useAuth();
  // Sync user role into role context at app level so guards work on first render
  const { userRole } = useRoleAccess();
  const { selectedWarehouseIds } = useWarehouseContext();
  const [currentPage, setCurrentPage] = useState('dashboard');
  const { t } = useTranslation();
  const navigationStrings = t('navigation.pages', { returnObjects: true });

  // Listen for navigation events from sidebar - must be before conditional returns
  /**
   * Effect: listens for custom 'navigate' events dispatched by `SideNavbar`.
   * Updates `currentPage` when navigation events are received.
   */
  useEffect(() => {
    const handleNavigation = (event) => {
      if (event.detail && event.detail.page) {
        setCurrentPage(event.detail.page);
      }
    };

    window.addEventListener('navigate', handleNavigation);
    return () => window.removeEventListener('navigate', handleNavigation);
  }, []);

  // Apply the logged-in user's saved UI language ('english' | 'hindi') on login.
  // The login response may only carry tokens (JWT has no `language`), so if the
  // auth user lacks it, fetch it from GET /users/me.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (user?.language) {
      setLocaleFromUserLanguage(user.language);
      return;
    }
    // The stored/JWT user often lacks `language`, and a persisted app_locale
    // from a previous user may be active. Force-refresh the profile so the
    // language reflects THIS user authoritatively (default English otherwise).
    let cancelled = false;
    profileService.getProfile(true).then((res) => {
      if (!cancelled && res?.success) {
        setLocaleFromUserLanguage(res.data?.language || 'english');
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [isAuthenticated, user?.language]);

  // Set default landing page based on role
  useEffect(() => {
    if (currentPage === 'dashboard' && user) {
      if (user?.role === 'SALES_STAFF' || user?.roles?.includes('SALES_STAFF')) {
        setCurrentPage('inquiries');
      } else if (user?.role === 'DELIVERY_SUPERVISOR' || user?.roles?.includes('DELIVERY_SUPERVISOR')) {
        setCurrentPage('dashboard-supervisor');
      } else if (user?.role === 'ACCOUNT_USER' || user?.role === USER_ROLES.ACCOUNT_USER || user?.roles?.includes('ACCOUNT_USER')) {
        setCurrentPage('delivery-challan');
      } else if (user?.role === USER_ROLES.OPERATIONS_MANAGER || user?.roles?.includes(USER_ROLES.OPERATIONS_MANAGER)) {
        setCurrentPage('orders');
      }
    }
  }, [user, currentPage]);

  // Show login if not authenticated.
  //
  // Gated on isInitializing, NOT isLoading. Submitting a login sets `loading`,
  // which used to fail this test and drop through to the loading screen below —
  // unmounting LoginForm mid-request. On failure it remounted with fresh state,
  // so the typed credentials were gone and the error message survived only as
  // long as the context error, which the next keystroke (or a browser autofill
  // into the now-empty fields) wiped. The form has its own in-place spinner and
  // disabled submit button, so it does not need the full-screen one.
  if (!isAuthenticated && !isInitializing) {
    return <Login />;
  }

  // Show loading while the session is being restored, or while the user/role is
  // still syncing after a successful sign-in.
  if (isInitializing || (isAuthenticated && !user) || (isAuthenticated && user && !userRole)) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-primary-600"></div>
          <p className="text-gray-500 font-medium">Loading session...</p>
        </div>
      </div>
    );
  }

  // Simple routing logic
  const getPageComponent = () => {
    switch (currentPage) {
      case 'dashboard':
      case 'dashboard-overview':
        return (
          <StaffGuard>
            <Dashboard />
          </StaffGuard>
        );
      case 'dashboard-sales':
        return (
          <StaffGuard>
            <SalesAnalytics />
          </StaffGuard>
        );
      case 'dashboard-delivery':
        return (
          <StaffGuard>
            <DeliveryAnalytics />
          </StaffGuard>
        );
      case 'dashboard-purchase':
        return (
          <StaffGuard>
            <PurchaseAnalytics />
          </StaffGuard>
        );
      case 'dashboard-inventory':
        return (
          <StaffGuard>
            <InventoryAnalytics />
          </StaffGuard>
        );
      case 'dashboard-customers':
        return (
          <StaffGuard>
            <CustomerAnalytics />
          </StaffGuard>
        );
      case 'dashboard-inquiries':
        return (
          <StaffGuard>
            <InquiryAnalytics />
          </StaffGuard>
        );
      case 'dashboard-financial':
        return (
          <StaffGuard>
            <FinancialAnalytics />
          </StaffGuard>
        );
      case 'dashboard-supervisor':
        return (
          <StaffGuard>
            <SupervisorAnalyticsPage />
          </StaffGuard>
        );
      case 'dashboard-challan-analytics':
        return (
          <StaffGuard>
            <ChallanAnalyticsPage />
          </StaffGuard>
        );
      case 'dashboard-assigned-to-challan':
        return (
          <AdminGuard>
            <AssignedToChallanPage />
          </AdminGuard>
        );
      case 'dashboard-movement-analytics':
        return (
          <AdminGuard>
            <MovementAnalyticsPage />
          </AdminGuard>
        );
      case 'dashboard-sales-analytics':
        return (
          <StaffGuard>
            <SalesStaffPage />
          </StaffGuard>
        );
      case 'users':
        return (
          <AdminGuard>
            <Users />
          </AdminGuard>
        );
      case 'ip-addresses':
        return (
          <AdminGuard>
            <IpAddressesPage />
          </AdminGuard>
        );
      case 'customers':
        return (
          <RoleGuard roles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.OPERATIONS_MANAGER]}>
            <CustomersPage />
          </RoleGuard>
        );
      case 'taxes':
        return (
          <AdminGuard>
            <TaxesPage />
          </AdminGuard>
        );
      case 'hsn-codes':
        return (
          <AdminGuard>
            <HsnCodesPage />
          </AdminGuard>
        );
      case 'locations':
        return (
          <AdminGuard>
            <LocationsPage />
          </AdminGuard>
        );
      case 'product-catalogue':
        return (
          <AdminGuard>
            <ProductCataloguePage />
          </AdminGuard>
        );
      case 'product-categories':
        return (
          <AdminGuard>
            <ProductCategoryPage />
          </AdminGuard>
        );
      case 'product-sizes':
        return (
          <AdminGuard>
            <ProductSizesPage />
          </AdminGuard>
        );
      case 'inquiries':
        return (
          <StaffGuard>
            <InquiriesPage />
          </StaffGuard>
        );
      case 'warehouses':
        return (
          <AdminGuard>
            <WarehousesPage />
          </AdminGuard>
        );
      case 'warehouse-inventory':
        return (
          <PermissionGuard permission={PERMISSIONS.VIEW_INVENTORY}>
            <WarehouseInventoryPage />
          </PermissionGuard>
        );
      case 'orders':
        return (
          <RoleGuard roles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.OPERATIONS_MANAGER, USER_ROLES.SALES_STAFF, USER_ROLES.ACCOUNT_USER]}>
            <OrdersPage />
          </RoleGuard>
        );

      case 'confirmed-orders':
        return (
          <RoleGuard roles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.OPERATIONS_MANAGER, USER_ROLES.SALES_STAFF, USER_ROLES.ACCOUNT_USER]}>
            <ConfirmedOrdersPage />
          </RoleGuard>
        );

      case 'payments-tracking':
        return (
          <RoleGuard roles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.ACCOUNT_USER]}>
            <PaymentsTrackingPage />
          </RoleGuard>
        );

      case 'purchase-orders':
        return (
          <StaffGuard>
            <PurchaseOrdersPage />
          </StaffGuard>
        );
      case 'purchase-advice':
        return (
          <StaffGuard>
            <PurchaseAdvicePage />
          </StaffGuard>
        );
      case 'purchase-challan':
        return (
          <RoleGuard roles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.OPERATIONS_MANAGER, USER_ROLES.SALES_STAFF, USER_ROLES.DELIVERY_SUPERVISOR, USER_ROLES.ACCOUNT_USER]}>
            <PurchaseChallanPage />
          </RoleGuard>
        );
      case 'purchase-challan-printed':
        return (
          <RoleGuard roles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.OPERATIONS_MANAGER, USER_ROLES.SALES_STAFF, USER_ROLES.ACCOUNT_USER]}>
            <PurchaseChallanPrintedPage />
          </RoleGuard>
        );
      case 'purchase-daybook':
        return (
          <RoleGuard roles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.OPERATIONS_MANAGER, USER_ROLES.SALES_STAFF, USER_ROLES.ACCOUNT_USER]}>
            <PurchaseDaybookPage />
          </RoleGuard>
        );
      case 'invoice-purchase':
        return (
          <RoleGuard roles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.OPERATIONS_MANAGER, USER_ROLES.ACCOUNT_USER]}>
            <InvoicePurchasePage />
          </RoleGuard>
        );

      case 'delivery-orders':
        return (
          <StaffGuard>
            <DeliveryOrdersPage />
          </StaffGuard>
        );

      case 'delivery-challan':
        return (
          <RoleGuard roles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.OPERATIONS_MANAGER, USER_ROLES.SALES_STAFF, USER_ROLES.DELIVERY_SUPERVISOR, USER_ROLES.ACCOUNT_USER]}>
            <DeliveryChallanPage />
          </RoleGuard>
        );
      case 'delivery-challan-printed':
        return (
          <RoleGuard roles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.OPERATIONS_MANAGER, USER_ROLES.SALES_STAFF, USER_ROLES.ACCOUNT_USER]}>
            <DeliveryChallanPrintedPage />
          </RoleGuard>
        );
      case 'unprinted-challans':
        return (
          <RoleGuard roles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.OPERATIONS_MANAGER, USER_ROLES.ACCOUNT_USER]}>
            <UnprintedChallansPage />
          </RoleGuard>
        );
      case 'dashboard-challan-print-variance':
        return (
          <RoleGuard roles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN]}>
            <ChallanPrintVariancePage />
          </RoleGuard>
        );

      case 'invoice-sales':
        return (
          <RoleGuard roles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.OPERATIONS_MANAGER, USER_ROLES.ACCOUNT_USER]}>
            <InvoiceSalesPage />
          </RoleGuard>
        );
      case 'sales-daybook':
        return (
          <RoleGuard roles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.OPERATIONS_MANAGER, USER_ROLES.SALES_STAFF, USER_ROLES.ACCOUNT_USER]}>
            <SalesDaybookPage />
          </RoleGuard>
        );
      case 'vehicle-tracking':
        return (
          <RoleGuard roles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.OPERATIONS_MANAGER, USER_ROLES.SALES_STAFF]}>
            <VehicleTrackingPage />
          </RoleGuard>
        );
      case 'profile':
        return <ProfilePage />;
      case 'settings':
      case 'channel-settings':
        return (
          <SuperAdminGuard>
            <ChannelSettingsPage />
          </SuperAdminGuard>
        );
      case 'tally-settings':
        return (
          <SuperAdminGuard>
            <TallySettingsPage />
          </SuperAdminGuard>
        );
      // Both of these sit behind require_super_admin_with_ip_check on the API, so ADMIN
      // would get a 403 on every call — the guard keeps that from ever being reachable.
      case 'reports':
        return (
          <SuperAdminGuard>
            <ReportsPage />
          </SuperAdminGuard>
        );
      case 'intelligence':
        return (
          <SuperAdminGuard>
            <IntelligencePage />
          </SuperAdminGuard>
        );

      case 'ai':
      case 'ai-chat':
        return (
          <AdminGuard>
            <AIChatPage />
          </AdminGuard>
        );
      case 'ai-purchase-plan':
        return (
          <AdminGuard>
            <AIPurchasePlanPage />
          </AdminGuard>
        );
      default:
        return <Dashboard />;
    }
  };

  const getPageTitle = () => {
    switch (currentPage) {
      case 'payments-tracking':
        return navigationStrings?.paymentsTracking;
      case 'dashboard':
      case 'dashboard-overview':
        return navigationStrings?.overview; // Or 'Overview', but navigationStrings.overview exists
      case 'dashboard-sales':
        return navigationStrings?.sales;
      case 'dashboard-delivery':
        return navigationStrings?.delivery;
      case 'dashboard-purchase':
        return navigationStrings?.purchase;
      case 'dashboard-inventory':
        return navigationStrings?.inventory;
      case 'dashboard-customers':
        return navigationStrings?.customers;
      case 'dashboard-inquiries':
        return navigationStrings?.inquiries;
      case 'dashboard-financial':
        return navigationStrings?.financial;
      case 'dashboard-supervisor':
        return navigationStrings?.supervisorAnalytics;
      case 'users':
        return navigationStrings?.users;
      case 'ip-addresses':
        return navigationStrings?.ipAddresses;
      case 'customers':
        return navigationStrings?.customers;
      case 'taxes':
        return navigationStrings?.taxes;
      case 'hsn-codes':
        return navigationStrings?.hsnCodes;
      case 'sales-daybook':
        return navigationStrings?.salesDaybook;
      case 'invoice-sales':
        return navigationStrings?.invoiceSales;
      case 'locations':
        return navigationStrings?.locations;
      case 'product-catalogue':
        return navigationStrings?.productCatalogue;
      case 'product-categories':
        return navigationStrings?.productCategory;
      case 'product-sizes':
        return navigationStrings?.productSize;
      case 'inquiries':
        return navigationStrings?.inquiries;
      case 'warehouses':
        return navigationStrings?.warehouses;
      case 'warehouse-inventory':
        return navigationStrings?.warehouseInventory;
      case 'orders':
        return navigationStrings?.orders;

      case 'confirmed-orders':
        return navigationStrings?.confirmedOrders;

      case 'purchase-orders':
        return navigationStrings?.purchaseOrders;
      case 'purchase-advice':
        return navigationStrings?.purchaseAdvice;
      case 'purchase-challan':
        return navigationStrings?.purchaseChallan;
      case 'purchase-challan-printed':
        return navigationStrings?.purchaseChallanPrinted;
      case 'purchase-daybook':
        return t('purchaseDaybook.page.header.title');
      case 'invoice-purchase':
        return t('invoicePurchase.page.header.title');

      case 'delivery-orders':
        return t('deliveryOrders.page.header.title');
      case 'dashboard-challan-analytics':
        return navigationStrings?.challanAnalytics;
      case 'dashboard-challan-print-variance':
        return navigationStrings?.challanPrintVariance;
      case 'dashboard-assigned-to-challan':
        return navigationStrings?.operations;
      case 'dashboard-movement-analytics':
        return navigationStrings?.movementAnalysis;
      case 'dashboard-sales-analytics':
        return navigationStrings?.salesStaff;
      case 'delivery-challan':
        return navigationStrings?.deliveryChallanNav;
      case 'delivery-challan-printed':
        return navigationStrings?.deliveryChallanPrinted;
      case 'vehicle-tracking':
        return navigationStrings?.vehicleTracking;
      case 'profile':
        return navigationStrings?.myProfile;
      case 'ai':
      case 'ai-chat':
        return navigationStrings?.aiChat;
      case 'ai-purchase-plan':
        return navigationStrings?.aiPurchasePlan;
      case 'settings':
        return navigationStrings?.settings || 'Settings';
      case 'channel-settings':
        return navigationStrings?.channelSettings || 'Settings';
      case 'tally-settings':
        return navigationStrings?.tallySettings || 'Tally Settings';
      case 'reports':
        return navigationStrings?.scheduledReports || 'Scheduled Reports';
      case 'intelligence':
        return navigationStrings?.intelligence || 'Analytics Intelligence';
      default:
        return navigationStrings?.dashboard;
    }
  };

  // Show main app with role-based content
  return (
    <ChannelSettingsProvider>
      <PrintSettingsProvider>
      <CoilSettingsProvider>
      <OrderSettingsProvider>
      <AppLayout pageTitle={getPageTitle()} currentPage={currentPage} setCurrentPage={setCurrentPage}>
        <div key={JSON.stringify(selectedWarehouseIds)}>
          {/* A render error on any screen used to blank the entire app. Scope it to the
              page and key by currentPage so navigating away clears a crash. */}
          <ErrorBoundary key={currentPage}>
            {getPageComponent()}
          </ErrorBoundary>
        </div>
      </AppLayout>
      </OrderSettingsProvider>
      </CoilSettingsProvider>
      </PrintSettingsProvider>
    </ChannelSettingsProvider>
  );
};

export default MainApp;